/**
 * input/controllers.ts — XR controller management + InputFrame
 *
 * xr-standard gamepad mapping (Quest Touch Plus):
 *   axes[0]=touchpad X, axes[1]=touchpad Y (unpopulated — always 0)
 *   axes[2]=thumbstick X, axes[3]=thumbstick Y  ← CORRECT
 *   buttons[0]=trigger, [1]=grip, [4]=A/X, [5]=B/Y
 */

import * as THREE from 'three';

export interface RawGamepadSnapshot {
  axes: number[];
  buttons: Array<{ pressed: boolean; value: number }>;
  handedness: 'left' | 'right';
}

export interface InputFrame {
  leftTrigger: number;
  rightTrigger: number;
  leftGrip: number;
  rightGrip: number;
  leftStickX: number;
  leftStickY: number;
  rightStickX: number;
  rightStickY: number;
  aPressed: boolean;
  bPressed: boolean;
  xPressed: boolean;
  yPressed: boolean;
}

const defaultInputFrame: InputFrame = {
  leftTrigger: 0,
  rightTrigger: 0,
  leftGrip: 0,
  rightGrip: 0,
  leftStickX: 0,
  leftStickY: 0,
  rightStickX: 0,
  rightStickY: 0,
  aPressed: false,
  bPressed: false,
  xPressed: false,
  yPressed: false,
};

let currentInput: InputFrame = { ...defaultInputFrame };

/**
 * Build InputFrame from raw gamepad snapshots.
 * Exported for unit testing — axes[2]=stickX, axes[3]=stickY per xr-standard.
 */
export function buildInputFrame(sources: RawGamepadSnapshot[]): InputFrame {
  const frame: InputFrame = { ...defaultInputFrame };

  for (const src of sources) {
    const gp = src;
    const isLeft = src.handedness === 'left';
    const ax = gp.axes;

    if (isLeft) {
      frame.leftTrigger = gp.buttons[0]?.value ?? 0;
      frame.leftGrip = gp.buttons[1]?.value ?? 0;
      frame.leftStickX = ax[2] ?? 0;   // thumbstick X
      frame.leftStickY = ax[3] ?? 0;   // thumbstick Y
      frame.xPressed = gp.buttons[4]?.pressed ?? false;
      frame.yPressed = gp.buttons[5]?.pressed ?? false;
    } else {
      frame.rightTrigger = gp.buttons[0]?.value ?? 0;
      frame.rightGrip = gp.buttons[1]?.value ?? 0;
      frame.rightStickX = ax[2] ?? 0;  // thumbstick X
      frame.rightStickY = ax[3] ?? 0;  // thumbstick Y
      frame.aPressed = gp.buttons[4]?.pressed ?? false;
      frame.bPressed = gp.buttons[5]?.pressed ?? false;
    }
  }

  return frame;
}

export function updateControllers(renderer: THREE.WebGLRenderer): InputFrame {
  if (!renderer.xr.isPresenting) {
    currentInput = { ...defaultInputFrame };
    return currentInput;
  }
  const session = renderer.xr.getSession();
  if (!session) {
    currentInput = { ...defaultInputFrame };
    return currentInput;
  }

  const snapshots: RawGamepadSnapshot[] = [];
  const sources = session.inputSources;
  for (let i = 0; i < sources.length; i++) {
    const src = sources[i];
    if (!src.gamepad) continue;
    snapshots.push({
      axes: [...src.gamepad.axes],
      buttons: src.gamepad.buttons.map(b => ({ pressed: b.pressed, value: b.value })),
      handedness: (src.handedness as 'left' | 'right') || 'right',
    });
  }

  currentInput = buildInputFrame(snapshots);
  return currentInput;
}

export function getInputFrame(): InputFrame {
  return currentInput;
}

// ── Controller init (for model display) ────────────────────────
let _initialized = false;

export function initControllers(_renderer: THREE.WebGLRenderer, _scene: THREE.Scene): void {
  if (_initialized) return;
  _initialized = true;
  console.log('[CTRL] Controllers initialized');
}
